import React from 'react'
import { reaction } from 'mobx'
import { inject, observer } from 'mobx-react'
import classnames from 'classnames'
import BoundaryList from './BoundaryList'
import { geoJSONBounds } from 'utils'

const BoundaryNode = ({
  selectionStore,
  uiStore,
  root,
  boundary,
  savingBoundaries
}) => {
  const hasNoChildren = boundary.boundaries.length === 0
  const finishedLoadingNode = !boundary.fetching_data && (boundary.loaded || boundary.zip_code_loaded || boundary.expanded)
  const isLeafNode = finishedLoadingNode && hasNoChildren
  let showByHasChildren = boundary.hasChildren
  if (typeof showByHasChildren === 'string') {
    showByHasChildren = showByHasChildren.toLowerCase() === 'true'
  }

  const shouldShowExpandIcon = isLeafNode
    ? false
    : (boundary.hasChildren !== undefined
        ? showByHasChildren
        : true)

  const isSelected = (selectionStore.item && selectionStore.item.selectedBoundaries || []).some(
    selectedBoundary => selectedBoundary.remote_db_id === boundary.id
  )

  const isHighlighted = selectionStore.item && selectionStore.item.selectedBoundary === boundary

  const handleExpand = (e) => {
    if (e) {
      e.stopPropagation()
    }
    boundary.expanded = !boundary.expanded
    uiStore.boundaryLayer = 'admin_boundaries_2018'
    if (boundary.expanded) {
      if (selectionStore.item && selectionStore.item.useZipCodes && !boundary.zip_code_loaded) {
        boundary.loadSubBoundaries(selectionStore.item.useZipCodes)
      }
      if (!boundary.loaded && selectionStore.item && !selectionStore.item.useZipCodes) {
        boundary.loadSubBoundaries(selectionStore.item.useZipCodes)
      }
    }
  }

  const handleSelectToggle = (e) => {
    e.stopPropagation()
    if (!selectionStore.item || typeof selectionStore.item.addBoundaryToSelection !== 'function') return
    if (e.target.checked) {
      const groupPk = selectionStore.data && selectionStore.data.group && selectionStore.data.group.pk
      selectionStore.item.addBoundaryToSelection({
        id: boundary.id,
        name: boundary.name,
        targetGroupId: groupPk,
        ids: boundary.ids,
        isZipCode: boundary.is_zip_code
      })
    } else {
      selectionStore.item.removeBoundaryFromSelection({ id: boundary.id })
    }
  }

  const handleRowClick = async (e) => {
    // Only zoom and render on map when clicking the item itself (not the checkbox or expand arrow)
    if (e.target.closest('.boundary-tree-checkbox') || e.target.closest('.boundary-tree-node__arrow')) {
      return
    }

    boundary.expanded = true
    if (selectionStore.item) {
      if (selectionStore.item.selectedBoundary) {
        selectionStore.item.selectedBoundary = null
      }
      selectionStore.item.selectedBoundary = boundary
    }

    // Set bounds immediately to prevent zooming out while geometry loads
    if (boundary.bounds && boundary.bounds.length > 0) {
      uiStore.setBounds(boundary.bounds)
    }

    if (!boundary.geoJSON) {
      await boundary.loadGeometry()
    }
    if (selectionStore.item && selectionStore.item.useZipCodes && !boundary.zip_code_loaded) {
      await boundary.loadSubBoundaries(selectionStore.item.useZipCodes)
    }
    if (!boundary.loaded && selectionStore.item && !selectionStore.item.useZipCodes) {
      await boundary.loadSubBoundaries(selectionStore.item.useZipCodes)
    }

    // Ensure bounds are set, fallback to calculating from geoJSON if needed
    if (boundary.bounds && boundary.bounds.length > 0) {
      uiStore.setBounds(boundary.bounds)
    } else if (boundary.geoJSON) {
      const calculatedBounds = geoJSONBounds(boundary.geoJSON)
      if (calculatedBounds) {
        uiStore.setBounds(calculatedBounds)
      }
    }
  }

  reaction(() => {
    if (selectionStore.item) {
      return selectionStore.item.useZipCodes
    }
    return false
  }, () => {
    boundary.expanded = false
  })

  return (
    <li className='boundary-tree-node'>
      <div
        className={classnames('boundary-tree-node__row', {
          'is-selected-boundary': isHighlighted
        })}
        onClick={handleRowClick}
      >
        {/* Arrow expand or spinner or placeholder */}
        {boundary.fetching_data
          ? (
            <span className='boundary-tree-node__arrow-placeholder' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className='spinner' style={{ width: 12, height: 12 }} />
            </span>
            )
          : shouldShowExpandIcon
            ? (
              <button
                type='button'
                className='boundary-tree-node__arrow'
                onClick={handleExpand}
                aria-label={boundary.expanded ? 'Collapse' : 'Expand'}
              >
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{
                    transform: boundary.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease'
                  }}
                >
                  <polyline points='9 18 15 12 9 6' />
                </svg>
              </button>
              )
            : (
              <span className='boundary-tree-node__arrow-placeholder' />
              )}

        {/* Checkbox: only selects item, does not zoom/render on map */}
        <input
          type='checkbox'
          className='boundary-tree-checkbox'
          checked={isSelected}
          onChange={handleSelectToggle}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Polygon / Hexagon icon */}
        <span className='boundary-tree-node__polygon-icon'>
          <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' />
          </svg>
        </span>

        {/* Boundary Name */}
        <span className='boundary-tree-node__name' title={boundary.name}>
          {boundary.name}
        </span>
      </div>

      {/* Nested Children */}
      {boundary.expanded && !boundary.fetching_data && boundary.boundaries && boundary.boundaries.length > 0 && (
        <div className='boundary-tree-node__children'>
          <BoundaryList root={root} parentNode={boundary} savingBoundaries={savingBoundaries} />
        </div>
      )}
    </li>
  )
}

export default inject('selectionStore', 'uiStore')(observer(BoundaryNode))
